import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Trophy, RotateCcw, ArrowRight, CheckCircle, XCircle,
  Loader2, Users,
} from "lucide-react";
import { toast } from "sonner";
import { Leaderboard } from "./Leaderboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ScoreResults({ score, total, percentage, results, questions, onRetry, mockId, timeTaken, trackEvent }) {
  const grade = percentage >= 80 ? "Excellent" : percentage >= 60 ? "Good" : percentage >= 40 ? "Fair" : "Keep Practicing";
  const gradeColor = percentage >= 80 ? "text-emerald-500" : percentage >= 60 ? "text-blue-500" : percentage >= 40 ? "text-amber-500" : "text-red-500";

  const [playerName, setPlayerName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [entryId, setEntryId] = useState(null);
  const [ctaVariant, setCtaVariant] = useState({ id: "default", button_text: "Create Free Account", button_color: "#e0482d" });

  useEffect(() => {
    axios.get(`${API}/sample-mocks/cta-variant`)
      .then(res => setCtaVariant(res.data))
      .catch(() => {});
  }, []);

  const handleLeaderboardSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/sample-mocks/${mockId}/leaderboard`, {
        player_name: playerName || "Anonymous",
        score, total, percentage,
        time_taken_seconds: timeTaken,
      });
      setEntryId(res.data.id);
      setSubmitted(true);
      toast.success("Score submitted to leaderboard!");
    } catch {
      toast.error("Failed to submit score");
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-5" data-testid="quiz-results">
      <Card className="rounded-2xl border-0 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 sm:p-8 text-center">
          <Trophy className="w-10 h-10 mx-auto mb-3 text-amber-400" />
          <p className="text-sm text-slate-400 mb-1">Your Score</p>
          <p className="text-5xl font-bold mb-1" data-testid="final-score">{percentage}%</p>
          <p className={`text-lg font-semibold ${gradeColor}`}>{grade}</p>
          <p className="text-sm text-slate-400 mt-2">
            {score} of {total} correct
            {timeTaken > 0 && <> &middot; {Math.floor(timeTaken / 60)}:{(timeTaken % 60).toString().padStart(2, "0")}</>}
          </p>
        </div>

        <CardContent className="p-5 space-y-4">
          {!submitted ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-testid="leaderboard-submit-form">
              <div className="flex items-center gap-2 flex-1">
                <Users className="w-4 h-4 text-slate-400 shrink-0" />
                <Input
                  placeholder="Enter your name for the leaderboard"
                  value={playerName}
                  onChange={e => setPlayerName(e.target.value)}
                  className="rounded-xl text-sm h-10"
                  maxLength={30}
                  data-testid="leaderboard-name-input"
                />
              </div>
              <Button onClick={handleLeaderboardSubmit} disabled={submitting}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-10 shrink-0" data-testid="submit-leaderboard-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trophy className="w-4 h-4 mr-1.5" /> Submit Score</>}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-center text-emerald-600 font-medium" data-testid="leaderboard-submitted">
              <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Score submitted to leaderboard!
            </p>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 border-t border-slate-100">
            <Button onClick={onRetry} variant="outline" className="rounded-xl" data-testid="retry-quiz-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Try Again
            </Button>
            <Button asChild className="text-white rounded-xl" style={{ backgroundColor: ctaVariant.button_color }}
              data-testid="results-signup-btn">
              <Link to="/register" onClick={() => trackEvent("signup_clicked", mockId, ctaVariant.id)}>
                {ctaVariant.button_text} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Leaderboard mockId={mockId} currentEntryId={entryId} />

      <div className="space-y-3">
        <h3 className="text-sm font-bold text-slate-700">Answer Review</h3>
        {results.map((r, i) => {
          const q = questions[i];
          return (
            <Card key={r.id} className={`rounded-xl border-l-4 ${r.is_correct ? "border-l-emerald-500" : "border-l-red-400"}`}
              data-testid={`result-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-2 mb-2">
                  {r.is_correct
                    ? <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                  <p className="text-sm font-medium text-slate-800">{q?.question}</p>
                </div>
                <div className="ml-7 space-y-1">
                  {(q?.options || []).map((opt, oi) => {
                    const isUser = r.user_answer === oi;
                    const isCorrect = r.correct_answer === oi;
                    let cls = "text-xs px-2 py-1 rounded-lg ";
                    if (isCorrect) cls += "bg-emerald-50 text-emerald-700 font-medium border border-emerald-200";
                    else if (isUser && !r.is_correct) cls += "bg-red-50 text-red-600 line-through border border-red-200";
                    else cls += "text-slate-500";
                    return (
                      <div key={oi} className={cls}>
                        {String.fromCharCode(65 + oi)}. {opt}
                        {isCorrect && " ✓"}
                        {isUser && !r.is_correct && " (your answer)"}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </motion.div>
  );
}
